import { useCallback, useEffect, useId, useRef, useState, type KeyboardEvent, type ReactNode } from 'react';
import AnchoredPop from '../../components/AnchoredPop.tsx';
import { useDismissMenu } from '../../lib/useDismissMenu.ts';
import { Check, ChevronDown } from '../../lib/icons.tsx';
import type { CategoryColour } from '../../../shared/api.ts';
import { swatchVar } from '../../../shared/palette.ts';

/**
 * One axis of the decks filter bar: "Folder · All ⌄".
 *
 * The bar has five of these (Status, Model, Folder, Updated, Sort) and they are
 * the same control five times, so the component knows nothing about decks — it
 * is handed a label, a value and a list, and hands back the value picked. Every
 * axis-specific decision (which options exist, what "active" means, what the
 * counts count) belongs to the route.
 *
 * Two things it does own, because they are the same for all five:
 *
 * - **The pill states its own answer, then inverts when that answer narrows
 *   the grid.** A row of five pills reading "All" has to be scannable for the
 *   one that isn't, and a colour or a weight change is not enough at that size:
 *   the fill flips to `--text` so the set axis reads as a solid mark in a row
 *   of outlines. The ink is `--bg`, not white, so the inversion works in both
 *   themes — in dark, `--text` is the pale one.
 * - **It is a listbox, not a menu.** The rows do not do five different things;
 *   they set one value, and exactly one of them is current. That is what the
 *   tick marks and what a screen reader is told, so the announcement is
 *   "Folder, listbox, Client work, selected" rather than five buttons.
 *
 * The popover is `AnchoredPop`, so it escapes the pill row's horizontal
 * scroller on a phone the same way the filmstrip's menus escape theirs.
 */

export type FilterPillOption<T> = {
  value: T;
  label: string;
  /** Shown right-aligned and muted. Omit where a count would be a lie. */
  count?: number;
  /** A category tint, rendered as a 7px dot before the label. */
  colour?: CategoryColour | null;
  disabled?: boolean;
};

export type FilterPillProps<T> = {
  /** The axis name, e.g. "Folder". Always visible: the value alone ("All")
   *  says nothing about what it is all of. */
  label: string;
  /** The current value, spelled for a reader rather than looked up by them. */
  valueLabel: string;
  value: T;
  options: FilterPillOption<T>[];
  onChange: (value: T) => void;
  /** A 15px leading glyph. */
  icon?: ReactNode;
  /** True when this axis is filtering. Inverts the pill. */
  active?: boolean;
  disabled?: boolean;
};

/** Where the keyboard should land when the list opens: the current value if it
 *  is still on offer, otherwise the first row that can be chosen. */
function initialIndex<T>(options: FilterPillOption<T>[], value: T): number {
  const current = options.findIndex((o) => o.value === value && !o.disabled);
  if (current >= 0) return current;
  return options.findIndex((o) => !o.disabled);
}

/** Step to the next selectable row, wrapping. Disabled rows are skipped rather
 *  than landed on, so arrowing never appears to stall. */
function step<T>(options: FilterPillOption<T>[], from: number, delta: number): number {
  const n = options.length;
  if (n === 0) return -1;
  for (let i = 1; i <= n; i += 1) {
    const next = (((from + delta * i) % n) + n) % n;
    if (!options[next]?.disabled) return next;
  }
  return from;
}

export default function FilterPill<T>({
  label,
  valueLabel,
  value,
  options,
  onChange,
  icon,
  active = false,
  disabled = false,
}: FilterPillProps<T>) {
  const btnRef = useRef<HTMLButtonElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const listId = useId();

  // Closing by ESCAPE or by picking a row returns the caret to the pill; closing
  // by clicking somewhere else must NOT, or the click would be followed by focus
  // jumping backwards out of whatever was just clicked.
  const close = useCallback((restoreFocus: boolean) => {
    setOpen(false);
    if (restoreFocus) btnRef.current?.focus();
  }, []);

  const dismiss = useCallback(() => close(false), [close]);
  // Both refs: the popover is portalled to <body>, so it is no longer inside
  // the pill and its own clicks would otherwise read as outside clicks.
  useDismissMenu([btnRef, popRef], dismiss, open);

  // The list, not its rows, holds the focus — one tab stop, and the active row
  // is named by `aria-activedescendant`. Focusing each row instead would make
  // every arrow press a focus change, which some screen readers narrate twice.
  //
  // The retry is load-bearing (2026-09-07). `AnchoredPop` renders `visibility:
  // hidden` for exactly one frame while it measures the popover and works out
  // where to put it, and a `visibility: hidden` element cannot take focus at
  // all — the call succeeded silently and left the caret on the pill, so the
  // list opened, said `aria-expanded`, and then ignored every arrow key.
  useEffect(() => {
    if (!open) return undefined;
    popRef.current?.focus();
    if (document.activeElement === popRef.current) return undefined;
    const frame = requestAnimationFrame(() => popRef.current?.focus());
    return () => cancelAnimationFrame(frame);
  }, [open]);

  function openList() {
    if (disabled) return;
    setActiveIndex(initialIndex(options, value));
    setOpen(true);
  }

  function pick(index: number) {
    const option = options[index];
    if (!option || option.disabled) return;
    onChange(option.value);
    close(true);
  }

  function onPillKeyDown(e: KeyboardEvent<HTMLButtonElement>) {
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
      e.preventDefault(); // Space would scroll the page, ArrowDown likewise
      openList();
    }
  }

  function onListKeyDown(e: KeyboardEvent<HTMLDivElement>) {
    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        setActiveIndex((i) => step(options, i, 1));
        break;
      case 'ArrowUp':
        e.preventDefault();
        setActiveIndex((i) => step(options, i, -1));
        break;
      case 'Home':
        e.preventDefault();
        setActiveIndex(step(options, -1, 1));
        break;
      case 'End':
        e.preventDefault();
        setActiveIndex(step(options, 0, -1));
        break;
      case 'Enter':
      case ' ':
        e.preventDefault();
        pick(activeIndex);
        break;
      case 'Escape':
        e.preventDefault();
        close(true);
        break;
      case 'Tab':
        // Tabbing away is a dismissal, not a pick — and the caret must carry on
        // to the next pill rather than be dragged back to this one.
        close(false);
        break;
      default:
        break;
    }
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={'filter-pill' + (active ? ' is-active' : '')}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listId : undefined}
        // The pill is the field, so it is labelled by the axis and announces the
        // current answer as its value.
        aria-label={`${label}: ${valueLabel}`}
        disabled={disabled}
        onClick={() => (open ? close(false) : openList())}
        onKeyDown={onPillKeyDown}
      >
        {icon && (
          <span className="filter-pill__icon" aria-hidden="true">
            {icon}
          </span>
        )}
        <span className="filter-pill__label" aria-hidden="true">
          {label}
        </span>
        <span className="filter-pill__value" aria-hidden="true">
          {valueLabel}
        </span>
        <span className="filter-pill__chevron" aria-hidden="true">
          <ChevronDown size={14} />
        </span>
      </button>
      {open && (
        <AnchoredPop
          anchor={btnRef}
          popRef={popRef}
          align="left"
          gap={6}
          className="filter-pop"
          role="listbox"
          id={listId}
          tabIndex={-1}
          aria-label={label}
          aria-activedescendant={activeIndex >= 0 ? `${listId}-${activeIndex}` : undefined}
          onKeyDown={onListKeyDown}
        >
          {options.map((option, index) => {
            const selected = option.value === value;
            return (
              <div
                key={String(option.value)}
                id={`${listId}-${index}`}
                role="option"
                aria-selected={selected}
                aria-disabled={option.disabled || undefined}
                className={
                  'filter-pop__row' +
                  (selected ? ' is-selected' : '') +
                  (index === activeIndex ? ' is-active' : '') +
                  (option.disabled ? ' is-disabled' : '')
                }
                // Pointer picking, on mouse DOWN, so the row acts before the
                // document-level dismissal listener sees the same press.
                onMouseDown={(e) => {
                  e.preventDefault(); // keep focus in the list, not on the row
                  pick(index);
                }}
                onMouseEnter={() => !option.disabled && setActiveIndex(index)}
              >
                <span className="filter-pop__tick" aria-hidden="true">
                  {selected && <Check size={14} />}
                </span>
                {/* A category's tint is a TOKEN NAME from the option, never a
                    literal — the same six the registry offers. */}
                {option.colour && (
                  <span
                    className="filter-pop__dot"
                    style={{ background: swatchVar(option.colour) }}
                    aria-hidden="true"
                  />
                )}
                <span className="filter-pop__label">{option.label}</span>
                {option.count !== undefined && <span className="filter-pop__count">{option.count}</span>}
              </div>
            );
          })}
        </AnchoredPop>
      )}
    </>
  );
}
